import { useMutation } from '@apollo/client'
import { trackUserEvent } from '@vend/utilities'
import { useEffect } from 'react'
import { WEB_REGISTER } from '../../../../../web-register.constant'
import { isTransactionFinished } from '../../helpers/transaction'
import {
  ProcessPaymentTransactionData,
  ProcessPaymentTransactionInput,
  ProcessRefundTransactionData,
  ProcessRefundTransactionInput,
  PROCESS_PAYMENT_TRANSACTION,
  PROCESS_REFUND_TRANSACTION,
  Transaction,
} from '../../queries/Transaction'
import { usePollTransaction } from '../use-poll-transaction/usePollTransaction'
import { SwiperErrorMessage } from '../use-swiper-payment-reducer/types'
import { useErrorMessages } from '../useErrorMessages'

interface TransactionData {
  registerSalePaymentID: string
  amount: number
  currency: string
  registerID: string
  retailerPaymentTypeID: string
  originalRegisterSalePaymentID?: string
  paymentTypeID: number
}

/**
 * Hook responsible for performing a swiper transaction.
 * Performs a refund if isRefund is true, otherwise will perform a payment.
 */
export const useTransaction = (
  isRefund: boolean,
  onComplete: (transaction: Transaction) => void,
  onError: (errorMessage: SwiperErrorMessage) => void,
  {
    registerSalePaymentID,
    amount,
    currency,
    registerID,
    retailerPaymentTypeID,
    originalRegisterSalePaymentID,
    paymentTypeID,
  }: TransactionData
) => {
  const { getSwiperErrorMessage } = useErrorMessages()
  const startPollingTransaction = usePollTransaction(
    registerSalePaymentID,
    onComplete,
    onError
  )

  const onMutationComplete = (transaction: Transaction) => {
    if (isTransactionFinished(transaction)) {
      onComplete(transaction)
      return
    }

    // if the transaction was not finished we poll until it is finished
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Swiper Polling For Transaction',
      {
        isReact: true,
        isRefund,
        transactionId: registerSalePaymentID,
        paymentTypeId: paymentTypeID,
      }
    )
    startPollingTransaction()
  }

  const [processRefund] = useMutation<
    ProcessRefundTransactionData,
    ProcessRefundTransactionInput
  >(PROCESS_REFUND_TRANSACTION, {
    variables: {
      input: {
        amount,
        currency,
        registerID,
        registerSalePaymentID,
        retailerPaymentTypeID,
        originalRegisterSalePaymentID,
      },
    },
    onCompleted: data => {
      onMutationComplete(data.processRefundTransaction)
    },
    onError: error => onError(getSwiperErrorMessage(error)),
  })

  const [processPayment] = useMutation<
    ProcessPaymentTransactionData,
    ProcessPaymentTransactionInput
  >(PROCESS_PAYMENT_TRANSACTION, {
    variables: {
      input: {
        registerID,
        retailerPaymentTypeID,
        registerSalePaymentID,
        amount,
        currency,
      },
    },
    onCompleted: data => {
      onMutationComplete(data.processPaymentTransaction)
    },
    onError: error => onError(getSwiperErrorMessage(error)),
  })

  // Immediately invoke either a refund or a payment
  useEffect(() => {
    if (isRefund) {
      processRefund()
      return
    }
    processPayment()
  }, [processPayment, processRefund, isRefund])
}
